import { makeStyles } from 'tss-react/mui'
import { fundingColors as C } from '../../theme/fundingColors'
import { FUNDING_FORM_FIELD_HEIGHT } from '../../theme/fundingSharedStyles'
import { fundingFont, fundingType } from '../../theme/fundingTypography'

/** Figma Desktop-856 text fields: Inter Regular 14/20, letter-spacing 0.25px. */
const fieldBody = {
  ...fundingType.body,
  color: C.darkText,
} as const

export const useStyles = makeStyles()(() => ({
  root: {
    width: '100%',
    minWidth: 0,
    fontFamily: fundingFont,
    '& .MuiFormControl-root': {
      width: '100%',
    },
    '& .MuiOutlinedInput-root': {
      boxSizing: 'border-box',
      minHeight: FUNDING_FORM_FIELD_HEIGHT,
      height: FUNDING_FORM_FIELD_HEIGHT,
      flexWrap: 'nowrap',
      alignItems: 'center',
      overflowX: 'auto',
      overflowY: 'hidden',
      padding: '8px 40px 8px 16px',
      gap: 4,
      ...fieldBody,
      backgroundColor: C.cardBg,
      borderRadius: 4,
      '& fieldset': {
        borderColor: C.buttonBorder,
      },
      '&:hover fieldset': {
        borderColor: C.buttonBorder,
      },
      '&.Mui-focused fieldset': {
        borderColor: C.linkBlue,
        borderWidth: 1,
      },
    },
    '& .MuiOutlinedInput-input, & .MuiAutocomplete-input': {
      padding: '2px 0',
      margin: 0,
      minWidth: 40,
      minHeight: 20,
      height: 'auto',
      flex: '1 0 auto',
      ...fieldBody,
      '&::placeholder': {
        color: C.mutedText,
        opacity: 1,
        fontWeight: 400,
      },
    },
    '& .MuiAutocomplete-endAdornment': {
      top: '50%',
      transform: 'translateY(-50%)',
      right: 8,
    },
    '& .MuiChip-root': {
      height: 24,
      maxWidth: 'none',
      flexShrink: 0,
      margin: '0 4px 0 0',
      fontFamily: fundingFont,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: '0.25px',
    },
    '& .MuiChip-label': {
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  /**
   * Multi-select: grow with chips (never clip below a fixed 44px).
   * Overrides the base single-line height / nowrap.
   */
  rootAllowWrap: {
    '& .MuiOutlinedInput-root': {
      height: 'auto !important',
      minHeight: FUNDING_FORM_FIELD_HEIGHT,
      flexWrap: 'wrap',
      alignContent: 'center',
      alignItems: 'center',
      overflowX: 'hidden',
      overflowY: 'visible',
      paddingTop: 8,
      paddingBottom: 8,
    },
    '& .MuiChip-root': {
      margin: '2px 4px 2px 0',
    },
    '& .MuiAutocomplete-endAdornment': {
      top: 12,
      transform: 'none',
    },
  },
  floatFieldRoot: {
    // minHeight only — never lock height (multi chips must expand).
    '& .MuiOutlinedInput-root': {
      minHeight: FUNDING_FORM_FIELD_HEIGHT,
      height: 'auto',
      paddingTop: 10,
      paddingBottom: 10,
      alignItems: 'center',
    },
    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
      transform: 'translate(14px, 12px) scale(1)',
    },
  },
  /** Floated label sits on the border — keep padding symmetric so value/chips stay centered. */
  floatFieldRootFloated: {
    '& .MuiOutlinedInput-root': {
      paddingTop: 10,
      paddingBottom: 10,
      alignItems: 'center',
      alignContent: 'center',
    },
    '& .MuiAutocomplete-endAdornment': {
      top: '50%',
      transform: 'translateY(-50%)',
    },
  },
  /**
   * Match BasicFloatInput / Desktop-856: resting label Inter 14/20 muted;
   * floated notch label Inter 12/16 — scale(1) so MUI's default 0.75 shrink
   * does not undersize Country/State vs Job title / Email.
   */
  floatLabel: {
    ...fundingType.fieldLabel,
    color: C.mutedText,
    backgroundColor: C.cardBg,
    padding: '0 2px',
    '&.MuiInputLabel-root': {
      ...fundingType.fieldLabel,
      color: C.mutedText,
    },
    '&.MuiInputLabel-shrink': {
      fontFamily: fundingFont,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '16px',
      letterSpacing: '0.25px',
      color: C.mutedText,
      // Override MUI shrink scale(0.75) so 12px stays 12px.
      transform: 'translate(14px, -8px) scale(1)',
    },
  },
  endAction: {
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: 4,
  },
  /** Popper listbox is portaled outside `.root` — same Inter 14/20 as field body. */
  listboxPaper: {
    ...fieldBody,
    '& .MuiAutocomplete-listbox': {
      ...fieldBody,
      padding: '4px 0',
    },
    '& .MuiAutocomplete-option, & li': {
      fontFamily: `${fundingFont} !important`,
      fontSize: '14px !important',
      fontWeight: '400 !important',
      lineHeight: '20px !important',
      letterSpacing: '0.25px',
      color: `${C.darkText} !important`,
      minHeight: 36,
    },
  },
  listbox: {
    ...fieldBody,
    padding: '4px 0',
  },
  option: {
    ...fieldBody,
  },
  optionTooltipTarget: {
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 0,
    maxWidth: '100%',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
  },
}))
